import { combineReducers } from "@reduxjs/toolkit";
import {
  createTransform,
  persistReducer,
  type PersistConfig,
} from "redux-persist";
import { createSparxsEditorSessionIndexedDbStorage } from "@/store/persistence/sparxsEditorSessionIndexedDbStorage";
import sparxsListReducer from "@/store/sparxsCatalog/listSlice";
import sparxsBlueprintCacheReducer from "@/store/sparxsCatalog/blueprintCacheSlice";
import {
  sparxsBlueprintCachePersistConfig,
  sparxsListPersistConfig,
  sparxsSearchIndexPersistConfig,
} from "@/store/sparxsCatalog/sparxsCatalogPersistConfigs";
import selectedInputFieldReducer from "@/features/sparxsEditor/store/slices/interaction/selectedInputField";
import activeSparxReducer from "@/features/sparxsEditor/store/slices/document/activeDocument";
import sparxReducer from "@/features/sparxsEditor/store/slices/document/documentSlice";
import sparxTeaserReducer from "@/features/sparxsEditor/store/slices/document/sparxTeaserSlice";
import sparxSessionReducer, {
  pickDirtySessionEntries,
  type SessionSparxEntry,
  type SparxSessionState,
} from "@/features/sparxsEditor/store/slices/session/sessionSlice";
import sessionPreferencesReducer from "@/features/sparxsEditor/store/slices/session/sessionPreferencesSlice";
import type { SessionPreferencesState } from "@/features/sparxsEditor/store/slices/session/sessionPreferencesSlice";
import sparxsSearchIndexReducer from "@/store/sparxsCatalog/searchIndexSlice";
import sparxsBlueprintFetchFailuresReducer from "@/store/sparxsCatalog/blueprintFetchFailuresSlice";
import catalogSearchReducer from "@/features/sparxsEditor/store/slices/catalog/catalogSearchSlice";
import blueprintLoadBoundaryReducer from "@/features/sparxsEditor/store/slices/blueprintLoadBoundarySlice";
import sparxReactionsReducer from "@/features/sparxsEditor/store/slices/reactions/sparxReactionsSlice";
import editorEntryBlockedReducer from "@/store/sparxsCatalog/editorEntryBlockedSlice";

const sessionPreferencesPersistConfig: PersistConfig<SessionPreferencesState> =
  {
    key: "editorSessionPreferences",
    storage: createSparxsEditorSessionIndexedDbStorage(),
  };

/** Writes only files with unsaved edits; everything else is rebuilt from the server. */
export const persistOnlyDirtySessionEntries = createTransform<
  Record<string, SessionSparxEntry>,
  Record<string, SessionSparxEntry>
>(pickDirtySessionEntries, (persisted) => persisted, {
  whitelist: ["byFileName"],
});

/**
 * Unsaved edits per file. Persisted so they survive the full-page redirect when the
 * session ends (and a reload); purged on explicit sign-out and when another user signs in.
 */
export const sparxSessionPersistConfig: PersistConfig<SparxSessionState> = {
  key: "sparxSession",
  storage: createSparxsEditorSessionIndexedDbStorage(),
  whitelist: ["byFileName"],
  transforms: [persistOnlyDirtySessionEntries],
};

export const sparxsEditorCoreEditorReducer = combineReducers({
  sparxsList: persistReducer(sparxsListPersistConfig, sparxsListReducer),
  sparxsBlueprintCache: persistReducer(
    sparxsBlueprintCachePersistConfig,
    sparxsBlueprintCacheReducer,
  ),
  sparx: sparxReducer,
  sparxTeaser: sparxTeaserReducer,
  sparxSession: persistReducer(sparxSessionPersistConfig, sparxSessionReducer),
  sessionPreferences: persistReducer(
    sessionPreferencesPersistConfig,
    sessionPreferencesReducer,
  ),
  selectedInputField: selectedInputFieldReducer,
  activeSparx: activeSparxReducer,
  sparxsSearchIndex: persistReducer(
    sparxsSearchIndexPersistConfig,
    sparxsSearchIndexReducer,
  ),
  sparxsBlueprintFetchFailures: sparxsBlueprintFetchFailuresReducer,
  blueprintLoadBoundary: blueprintLoadBoundaryReducer,
  editorEntryBlocked: editorEntryBlockedReducer,
  catalogSearch: catalogSearchReducer,
  sparxReactions: sparxReactionsReducer,
});

export type SparxsEditorCoreEditorState = ReturnType<
  typeof sparxsEditorCoreEditorReducer
>;
